/**
 * Merge custom.linked_products into the product used by the width × length chart.
 * The page product stays the source for title, images, and type.
 * Sibling variants are aligned onto the same Width / Length / Material axes.
 */
(function () {
  'use strict';

  /**
   * @param {unknown} value
   * @returns {string}
   */
  function normalizeShopifyId(value) {
    var raw = String(value == null ? '' : value).trim();
    if (!raw) return '';
    var gid = raw.match(/(\d+)\s*$/);
    return gid ? gid[1] : raw;
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  function normalizeKey(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ');
  }

  /**
   * @param {string} key
   * @returns {boolean}
   */
  function isMaterialAxisKey(key) {
    return /(material|karat|carat|metal|purity|jewellery|jewelry)/.test(normalizeKey(key));
  }

  /**
   * @param {string} key
   * @returns {boolean}
   */
  function isWidthAxisKey(key) {
    var normalized = normalizeKey(key);
    if (isMaterialAxisKey(normalized)) return false;
    return /(width|thickness|gauge|\bmm\b|diameter|\bdia\b)/.test(normalized);
  }

  /**
   * @param {string} key
   * @returns {boolean}
   */
  function isLengthAxisKey(key) {
    return /(length|inch|inches|\blen\b)/.test(normalizeKey(key));
  }

  /**
   * @param {unknown} value
   * @returns {boolean}
   */
  function isKaratLikeValue(value) {
    return /^\d{1,2}\s*k(?:t)?$/i.test(String(value == null ? '' : value).trim());
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  function normalizeWidthLabel(value) {
    var raw = String(value == null ? '' : value).trim();
    if (!raw || raw === '—') return raw;
    if (isKaratLikeValue(raw)) return raw;
    var mmMatch = raw.match(/^(\d+(?:\.\d+)?)\s*mm$/i);
    if (mmMatch) return mmMatch[1];
    return raw;
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  function normalizeLengthLabel(value) {
    var raw = String(value == null ? '' : value).trim();
    if (!raw || raw === '—') return raw;
    var stripped = raw.replace(/\s*(inches|inch|in|")\s*$/i, '').replace(/["']/g, '').trim();
    return stripped || raw;
  }

  /**
   * @param {Record<string, unknown>} product
   * @returns {string}
   */
  function extractWidthFromProduct(product) {
    if (!product || typeof product !== 'object') return '';
    /** @type {string[]} */
    var texts = [];
    if (product.title) texts.push(String(product.title));
    if (product.handle) texts.push(String(product.handle).replace(/[-_]+/g, ' '));
    if (Array.isArray(product.tags)) {
      product.tags.forEach(function (tag) {
        if (tag != null && String(tag).trim()) texts.push(String(tag));
      });
    }
    for (var i = 0; i < texts.length; i += 1) {
      var match = texts[i].match(/(\d+(?:\.\d+)?)\s*mm\b/i);
      if (match) return normalizeWidthLabel(match[1]);
    }
    return '';
  }

  /**
   * @param {Record<string, unknown>} product
   * @returns {string}
   */
  function fallbackMaterialLabel(product) {
    /** @type {string[]} */
    var texts = [];
    if (product && product.title) texts.push(String(product.title));
    if (product && product.handle) texts.push(String(product.handle).replace(/[-_]+/g, ' '));
    if (product && Array.isArray(product.tags)) {
      product.tags.forEach(function (tag) {
        if (tag != null && String(tag).trim()) texts.push(String(tag));
      });
    }
    for (var i = 0; i < texts.length; i += 1) {
      var match = texts[i].match(/\b\d{1,2}\s*k(?:t)?\b/i);
      if (match) return match[0].replace(/\s+/g, '').toUpperCase();
    }
    return 'Other';
  }

  /**
   * @param {unknown} product
   * @returns {string[]}
   */
  function optionNamesOf(product) {
    var source = product && typeof product === 'object' ? /** @type {Record<string, unknown>} */ (product).options : [];
    var options = Array.isArray(source) ? source : [];
    return options
      .map(function (opt) {
        if (typeof opt === 'string') return opt.trim();
        if (opt && typeof opt === 'object' && 'name' in opt && opt.name != null) return String(opt.name).trim();
        return '';
      })
      .filter(Boolean);
  }

  /**
   * @param {Record<string, unknown>} variant
   * @param {number} idx
   * @returns {string}
   */
  function variantOptionValue(variant, idx) {
    if (!variant || idx < 0) return '';
    var direct = variant['option' + String(idx + 1)];
    if (direct != null && String(direct).trim()) return String(direct).trim();
    var options = Array.isArray(variant.options) ? variant.options : [];
    return options[idx] != null ? String(options[idx]).trim() : '';
  }

  /**
   * @param {Record<string, unknown>} product
   * @returns {{ width: number, length: number, material: number }}
   */
  function axesForProduct(product) {
    var names = optionNamesOf(product);
    /** @type {{ width: number, length: number, material: number }} */
    var map = { width: -1, length: -1, material: -1 };
    names.forEach(function (name, idx) {
      var key = normalizeKey(name);
      if (map.material < 0 && isMaterialAxisKey(key)) map.material = idx;
      if (map.width < 0 && isWidthAxisKey(key)) map.width = idx;
      if (map.length < 0 && isLengthAxisKey(key)) map.length = idx;
    });
    if (map.length < 0) {
      for (var i = 0; i < names.length; i += 1) {
        if (i === map.width || i === map.material) continue;
        if (isMaterialAxisKey(normalizeKey(names[i]))) continue;
        map.length = i;
        break;
      }
    }
    return map;
  }

  /**
   * @param {Record<string, unknown>[]} products
   * @returns {Array<'width'|'length'|'material'>}
   */
  function canonicalAxes(products) {
    /** @type {Array<'width'|'length'|'material'>} */
    var axes = [];
    /**
     * @param {'width'|'length'|'material'|null} axis
     */
    function add(axis) {
      if (!axis || axes.indexOf(axis) >= 0) return;
      axes.push(axis);
    }

    var primaryMap = axesForProduct(products[0]);
    optionNamesOf(products[0]).forEach(function (_name, idx) {
      if (primaryMap.width === idx) add('width');
      if (primaryMap.length === idx) add('length');
      if (primaryMap.material === idx) add('material');
    });

    products.forEach(function (product) {
      var map = axesForProduct(product);
      if (map.width >= 0 || extractWidthFromProduct(product)) add('width');
      if (map.length >= 0) add('length');
      if (map.material >= 0) add('material');
    });

    return axes.slice(0, 3);
  }

  /**
   * @param {'width'|'length'|'material'} axis
   * @returns {string}
   */
  function axisLabel(axis) {
    if (axis === 'width') return 'Width';
    if (axis === 'length') return 'Length';
    return 'Material';
  }

  /**
   * @param {Record<string, unknown>} product
   * @param {Record<string, unknown>} variant
   * @param {'width'|'length'|'material'} axis
   * @returns {string}
   */
  function readAxisValue(product, variant, axis) {
    var map = axesForProduct(product);
    var idx = map[axis];
    var raw = idx >= 0 ? variantOptionValue(variant, idx) : '';
    if (axis === 'width') {
      if (raw) return normalizeWidthLabel(raw);
      return extractWidthFromProduct(product);
    }
    if (axis === 'length') return raw ? normalizeLengthLabel(raw) : '';
    if (raw) return raw;
    return fallbackMaterialLabel(product);
  }

  /**
   * @param {unknown} product
   * @returns {string}
   */
  function familyKey(product) {
    if (!product || typeof product !== 'object') return '';
    var record = /** @type {Record<string, unknown>} */ (product);
    var id = normalizeShopifyId(record.id);
    if (id) return 'id:' + id;
    var handle = String(record.handle || '').trim().toLowerCase();
    return handle ? 'handle:' + handle : '';
  }

  /**
   * @param {Record<string, unknown>} product
   * @returns {string[]}
   */
  function collectProductIds(product) {
    /** @type {string[]} */
    var ids = [];
    /** @type {Record<string, boolean>} */
    var seen = Object.create(null);
    /**
     * @param {unknown} value
     */
    function add(value) {
      var id = normalizeShopifyId(value);
      if (!id || seen[id]) return;
      seen[id] = true;
      ids.push(id);
    }
    var listed = product && product.__linkedProductIds;
    if (Array.isArray(listed) && listed.length) {
      listed.forEach(add);
      return ids;
    }
    add(product && product.id);
    return ids;
  }

  /**
   * @param {Record<string, unknown>} product
   * @returns {string[]}
   */
  function collectHandles(product) {
    /** @type {string[]} */
    var handles = [];
    /** @type {Record<string, boolean>} */
    var seen = Object.create(null);
    /**
     * @param {unknown} value
     */
    function add(value) {
      var handle = String(value || '').trim();
      if (!handle) return;
      var key = handle.toLowerCase();
      if (seen[key]) return;
      seen[key] = true;
      handles.push(handle);
    }
    add(product && product.handle);
    var listed = product && product.__linkedHandles;
    if (Array.isArray(listed)) listed.forEach(add);
    return handles;
  }

  /**
   * @param {Record<string, unknown>} primary
   * @param {unknown} linkedProducts
   * @returns {Record<string, unknown>}
   */
  function mergeFamily(primary, linkedProducts) {
    if (!primary || typeof primary !== 'object') return primary;
    if (primary.__linkedFamilyMerged === true) return primary;

    /** @type {Record<string, unknown>[]} */
    var family = [primary];
    /** @type {Record<string, boolean>} */
    var seen = Object.create(null);
    var primaryKey = familyKey(primary);
    if (primaryKey) seen[primaryKey] = true;

    var linked = Array.isArray(linkedProducts) ? linkedProducts : [];
    linked.forEach(function (entry) {
      if (!entry || typeof entry !== 'object') return;
      var key = familyKey(entry);
      if (!key || seen[key]) return;
      seen[key] = true;
      family.push(/** @type {Record<string, unknown>} */ (entry));
    });

    if (family.length < 2) return primary;

    var axes = canonicalAxes(family);
    if (!axes.length) return primary;

    /** @type {Record<string, unknown>[]} */
    var variants = [];
    /** @type {Record<string, boolean>} */
    var seenVariantIds = Object.create(null);
    /** @type {string[]} */
    var ids = [];
    /** @type {string[]} */
    var handles = [];

    family.forEach(function (source) {
      var sourceId = normalizeShopifyId(source.id);
      if (sourceId) ids.push(sourceId);
      var handle = String(source.handle || '').trim();
      if (handle) handles.push(handle);
      var sourceVariants = Array.isArray(source.variants) ? source.variants : [];
      sourceVariants.forEach(function (variant) {
        if (!variant || typeof variant !== 'object') return;
        var variantId = normalizeShopifyId(variant.id);
        if (!variantId || seenVariantIds[variantId]) return;
        seenVariantIds[variantId] = true;
        /** @type {Record<string, unknown>} */
        var aligned = Object.assign({}, variant);
        /** @type {string[]} */
        var optionValues = [];
        axes.forEach(function (axis, index) {
          var value = readAxisValue(source, /** @type {Record<string, unknown>} */ (variant), axis) || '—';
          optionValues.push(value);
          aligned['option' + String(index + 1)] = value;
        });
        for (var extra = axes.length; extra < 3; extra += 1) {
          aligned['option' + String(extra + 1)] = null;
        }
        aligned.options = optionValues;
        aligned.product_id = sourceId || normalizeShopifyId(primary.id);
        aligned.productId = aligned.product_id;
        variants.push(aligned);
      });
    });

    if (!variants.length) return primary;

    var merged = Object.assign({}, primary);
    merged.options = axes.map(axisLabel);
    merged.variants = variants;
    merged.__linkedFamilyMerged = true;
    merged.__linkedProductIds = ids;
    merged.__linkedHandles = handles;
    return merged;
  }

  window.AoneLinkedProducts = {
    mergeFamily: mergeFamily,
    collectProductIds: collectProductIds,
    collectHandles: collectHandles,
  };
})();
